import { useState } from 'react';

import { tenantLogoUrl } from '@/shared/branding/logoUrl';

/**
 * The tenant's mark (§57.16, `AM-174`): its own logo, from the one unauthenticated route of `ISO-16`,
 * when `hasLogo` says there is one; a monogram on a fixed tint otherwise. `tenantSlug` is what the
 * public URL is built from - every call site already carries it (`session.activeTenant.tenantSlug`,
 * the membership list, or the appearance query). The tenant selector, before a tenant is active,
 * gets `hasLogo`/`logoVersion` from the memberships of the session: without them it could not tell a
 * tenant's logo from the neutral default the route answers with, and showed that default in place
 * of the monogram. Where a caller still has no answer (`undefined`) the `<img>` is attempted, and an
 * `onError` - never fired by the endpoint's own no-logo case, which answers `200` with the default
 * mark - falls back to the monogram only for an actual failure to reach it. The logo is scaled to fit the square
 * whole (`contain`), never cropped: a wide or tall one shows complete, with room left on its sides.
 */
export function TenantMark({
  name,
  tenantSlug,
  hasLogo,
  logoVersion,
  size = 24,
}: {
  name: string;
  tenantSlug?: string;
  hasLogo?: boolean;
  logoVersion?: string | null;
  size?: number;
}) {
  const [imageFailed, setImageFailed] = useState(false);
  const showImage = tenantSlug !== undefined && hasLogo !== false && !imageFailed;

  if (showImage) {
    return (
      <img
        src={tenantLogoUrl(tenantSlug, logoVersion)}
        alt=""
        width={size}
        height={size}
        style={{
          borderRadius: Math.round(size / 4),
          objectFit: 'contain',
          flex: 'none',
        }}
        onError={() => setImageFailed(true)}
      />
    );
  }

  const initials = name
    .split(/\s+/)
    .filter((part) => part.length > 0)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');

  return (
    <span
      aria-hidden="true"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: size,
        height: size,
        borderRadius: Math.round(size / 4),
        background: 'var(--sl-steel-700)',
        color: '#FFFFFF',
        fontWeight: 600,
        fontSize: Math.round(size * 0.44),
        letterSpacing: '0.01em',
        flex: 'none',
      }}
    >
      {initials}
    </span>
  );
}
